import React, { useState, useRef } from 'react';
import Localize from 'react-intl-universal';

import { get } from 'lodash-es';

import { alpha } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';

import { httpStatusCodes } from '@common/Constants';
import { CUSTOMER_LOYALTY_EVENTS } from '@common/network';
import ParticipantDialogForm from '@pages/BookingProcess/Participants/ParticipantDialogForm/ParticipantDialogForm';
import { postByPathAndData } from '@services/BaseApi';

import ConfirmationDialog, { StyledCustomDialog } from './ConfirmationDialog';
import { formatDataForShowInterest, useShowInterestOptions } from '../Util';

const StyledSaveButton = styled(Button)(({ theme }) => ({
  minWidth: '10rem',
  marginRight: 0,
  textTransform: 'none',
  fontWeight: '700',
  fontSize: '14px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': { backgroundColor: alpha(get(theme.palette.primary, 'main'), 0.7), boxShadow: 'none' },
  '&.Mui-disabled': { color: theme.palette.common.white, opacity: '0.5' },
  [theme.breakpoints.up('sm')]: { marginRight: '4rem' }
}));
const StyledCancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: '700',
  color: theme.palette.primary.main
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: '0.3rem',
  fontWeight: '700',
  fontSize: '1.25rem',
  color: theme.palette.primary.contrastText
}));

const ShowInterestDialog = ({ openShowInterestDialog, setOpenShowInterestDialog, event }) => {
  const [isFormValid, setIsFormValid] = useState(true);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [hasErrorMessage, setHasErrorMessage] = useState(null);
  const inputRef = useRef(null);
  useShowInterestOptions();

  const handleClose = () => {
    setOpenShowInterestDialog(false);
  };
  const handleSaveParticipant = () => {
    inputRef.current.click();
  };

  const showInterestSubmit = async (values) => {
    await postByPathAndData({
      path: CUSTOMER_LOYALTY_EVENTS.SHOW_INTEREST_POST,
      data: formatDataForShowInterest({
        ...values,
        eventId: event?.id,
        isEventFree: !event?.hasPricesAndDiscountsFeature
      }),
      pathVariables: { uuid: event.uuid },
      temApi: true
    })
      .then((response) => {
        if (response?.status != httpStatusCodes.created) {
          setHasErrorMessage({ message: response?.response?.data?.message });
        }
      })
      .catch((err) => {
        setHasErrorMessage({ message: err?.message });
      })
      .finally(() => {
        setShowSnackbar(true);
        setOpenShowInterestDialog(false);
      });
  };

  return (
    <>
      <StyledCustomDialog
        open={openShowInterestDialog}
        onClose={handleClose}
        fullWidth={true}
        maxWidth='md'
      >
        <StyledDialogTitle id='alert-dialog-title' sx={{ mt: 0.5, pl: '3.5rem', pr: '3.5rem' }}>
          {Localize.get('EventDetails.showInterest')}
        </StyledDialogTitle>

        <DialogContent
          sx={{
            paddingTop: 0,
            pl: { xs: '0.5rem', sm: '3.5rem' },
            pr: { xs: '0.5rem', sm: '3.5rem' }
          }}
        >
          <ParticipantDialogForm
            mode={'create'}
            inputRef={inputRef}
            setOpen={setOpenShowInterestDialog}
            setIsFormValid={setIsFormValid}
            isCreateNewParticipant={false}
            isShowInterest={true}
            showInterestSubmit={showInterestSubmit}
            isEventFree={!event?.hasPricesAndDiscountsFeature || false}
          />
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: (theme) => theme.palette.booking.dialogFooter,
            justifyContent: { xs: 'center', sm: 'flex-end' }
          }}
        >
          <StyledCancelButton onClick={handleClose}>{Localize.get('App.close')}</StyledCancelButton>
          <StyledSaveButton onClick={handleSaveParticipant} disabled={!isFormValid}>
            {Localize.get('Booking.saveParticipant')}
          </StyledSaveButton>
        </DialogActions>
      </StyledCustomDialog>
      <ConfirmationDialog
        hasErrorMessage={hasErrorMessage}
        event={event}
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
        setHasErrorMessage={setHasErrorMessage}
      />
    </>
  );
};

export default ShowInterestDialog;
